import { useState } from "react";
import { ActionList, Button, ButtonGroup, Collapsible, Popover, Text, Tooltip } from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import { PiBracketsCurlyBold } from "react-icons/pi";
import { AiOutlineAlignLeft, AiOutlineAlignCenter, AiOutlineAlignRight } from "react-icons/ai";
import { useFormContext, useWatch } from "react-hook-form";
import { FormCheckbox, FormTextField, FormToggle } from "../FormFields";
import IconUploader from "../IconUploader/IconUploader";
import styles from "./RHFSavingSummary.module.css";
import useIsMobile from "../../hooks/useIsMobile";

const ALIGNMENT_OPTIONS = [
    {
        value: "left",
        icon: (
            <span className={styles.alignmentIcon}>
                <AiOutlineAlignLeft size={20} />
            </span>
        ),
    },
    {
        value: "center",
        icon: (
            <span className={styles.alignmentIcon}>
                <AiOutlineAlignCenter size={20} />
            </span>
        ),
    },
    {
        value: "right",
        icon: (
            <span className={styles.alignmentIcon}>
                <AiOutlineAlignRight size={20} />
            </span>
        ),
    },
];

export type RHFSavingSummaryProps = {
    fieldPrefix?: string;
};

const RHFSavingSummary = ({ fieldPrefix = "savings_summary" }: RHFSavingSummaryProps) => {
    const { isMobile } = useIsMobile();
    const { control, setValue, getValues } = useFormContext();
    const [expanded, setExpanded] = useState(false);
    const [variablePopoverActive, setVariablePopoverActive] = useState(false);

    const titleFieldName = `${fieldPrefix}.title`;
    const textAlign = useWatch({ control, name: `${fieldPrefix}.text_align` });

    const handleInsertVariable = (variable: string) => {
        const current = getValues(titleFieldName) || "";
        setValue(titleFieldName, `${current} ${variable}`, { shouldDirty: true });
        setVariablePopoverActive(false);
    };

    return (
        <>
            <div className={styles.header} onClick={() => setExpanded((prev) => !prev)}>
                <div className={styles.titleWithToggle}>
                    <span>Savings summary</span>
                    <div onClick={(e) => e.stopPropagation()}>
                        <FormToggle name={`${fieldPrefix}.status`} />
                    </div>
                </div>
                <div className={styles.chevron} style={{ transform: expanded ? "rotate(90deg)" : "rotate(270deg)" }}>
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>

            <Collapsible
                open={expanded}
                id="savings-summary-collapsible"
                transition={{ duration: "300ms", timingFunction: "ease-in-out" }}
                expandOnPrint
            >
                <div className={styles.content}>
                    <div className={styles.inputWrapper}>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "4px",
                            }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>Title</div>
                            <Popover
                                active={variablePopoverActive}
                                onClose={() => setVariablePopoverActive(false)}
                                activator={
                                    <div
                                        onClick={() => setVariablePopoverActive((prev) => !prev)}
                                        className={styles.variableTrigger}
                                    >
                                        <Tooltip content="Insert variable">
                                            <PiBracketsCurlyBold size={18} />
                                        </Tooltip>
                                    </div>
                                }
                            >
                                <ActionList
                                    items={[
                                        { content: "Icon", onAction: () => handleInsertVariable("{icon}") },
                                        {
                                            content: "Saved amount",
                                            onAction: () => handleInsertVariable("{saved_amount}"),
                                        },
                                        {
                                            content: "Saved percentage",
                                            onAction: () => handleInsertVariable("{saved_percentage}"),
                                        },
                                        {
                                            content: "Total price",
                                            onAction: () => handleInsertVariable("{total_price}"),
                                        },
                                    ]}
                                />
                            </Popover>
                        </div>
                        <FormTextField
                            name={titleFieldName}
                            label="Title"
                            labelHidden
                            placeholder="You're saving {saved_amount} on this order"
                            autoComplete="off"
                        />
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
                        <div className={styles.alignmentRow}>
                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                                Alignment
                            </Text>
                            <ButtonGroup variant="segmented">
                                {ALIGNMENT_OPTIONS.map((opt) => (
                                    <Button
                                        key={opt.value}
                                        icon={opt.icon}
                                        accessibilityLabel={`Align ${opt.value}`}
                                        pressed={textAlign === opt.value}
                                        onClick={() =>
                                            setValue(`${fieldPrefix}.text_align`, opt.value, { shouldDirty: true })
                                        }
                                    />
                                ))}
                            </ButtonGroup>
                        </div>

                        <IconUploader
                            iconTypeName={`${fieldPrefix}.icon_type`}
                            iconUrlName={`${fieldPrefix}.icon_url`}
                            sizeFieldName="bundles_design.savings_summary_font_size"
                        />
                    </div>

                    <FormCheckbox
                        name={`${fieldPrefix}.include_free_gift_value`}
                        label="Include free gift value in savings"
                    />

                    <FormCheckbox name={`${fieldPrefix}.hide_when_no_savings`} label="Hide when there are no savings" />
                </div>
            </Collapsible>
        </>
    );
};

export default RHFSavingSummary;
